import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { beLinkCOS } from '@be-link/cos';
import { beLinkHttp } from '@be-link/http';
import VConsole from 'vconsole';

import { getUrlParams } from './utils/url';

import { router } from '@/router';
import { initApmplusMonitor, updateApmplusUserConfig } from '@/utils/apmplusMonitor';
import { destroyFeishuErrorReporter, initFeishuErrorReporter } from '@/utils/feishu';
import { getUserInfoLocal, clearWelfareUnionIdLocal } from '@/utils/storage';
import '@/styles/reset.less';
import { setResponseInterceptor } from '@/api/respInterceptor';

const ENABLE_REPORT_ENVS = ['production', 'test'];

const viteNodeEnv = import.meta.env['VITE_NODE_ENV'];
const viteBffApiUrl = import.meta.env['VITE_BFF_API_URL'];
const encryptionKey = import.meta.env['VITE_TOKEN_ENCRYPTION_KEY'];
const encryptionIV = import.meta.env['VITE_TOKEN_ENCRYPTION_IV'];

const timeSyncUrl = `${viteBffApiUrl}/api/system/get-current-time`;

beLinkCOS.init({ mode: viteNodeEnv });

function App() {
  if (import.meta.env['VITE_NODE_ENV'] !== 'production') {
    new VConsole();
  }

  beLinkHttp.init({
    baseURL: viteBffApiUrl,
    getToken: () => {
      const userInfoLocal = getUserInfoLocal();
      const urlParams = new URLSearchParams(window.location.search);
      return userInfoLocal?.token || urlParams.get('token') || '';
    },
    getUserId: () => {
      const userInfoLocal = getUserInfoLocal();
      const urlParams = new URLSearchParams(window.location.search);
      return userInfoLocal?.userId || urlParams.get('userId') || '';
    },
    timeout: 100000,
    timeSync: { enabled: true, syncUrl: timeSyncUrl },
    encryption: { key: encryptionKey, iv: encryptionIV },
    onError: setResponseInterceptor,
  });

  if (ENABLE_REPORT_ENVS.includes(import.meta.env['VITE_NODE_ENV'])) {
    initApmplusMonitor();
    const userInfo = getUserInfoLocal();
    const params = getUrlParams();
    const userId = userInfo?.userId || params.userId;
    if (userId) {
      updateApmplusUserConfig({ userId });
    }
  }

  useEffect(() => {
    // 只在生产环境初始化飞书错误上报
    if (ENABLE_REPORT_ENVS.includes(import.meta.env.VITE_NODE_ENV)) {
      initFeishuErrorReporter({
        webhookUrls: {
          production: import.meta.env.VITE_FEISHU_WEBHOOK_URL || '',
          test: import.meta.env.VITE_FEISHU_WEBHOOK_URL || '',
        },
        environment: import.meta.env.VITE_NODE_ENV,
        getUserInfo: () => {
          const params = getUrlParams();
          const userInfo = getUserInfoLocal();
          return userInfo?.userId
            ? {
                userId: userInfo.userId || params.userId,
                nickname: userInfo.nickname || params.nickname,
                unionId: userInfo.unionId || params.unionId,
                openId: userInfo.openId || params.openId,
              }
            : null;
        },
        sampleRate: 1.0,
        enabled: (environment) => ENABLE_REPORT_ENVS.includes(environment),
      });
    }

    // 监听页面卸载事件，清除分享归因 unionId
    const handleBeforeUnload = () => {
      clearWelfareUnionIdLocal();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      if (ENABLE_REPORT_ENVS.includes(import.meta.env.VITE_NODE_ENV)) {
        destroyFeishuErrorReporter();
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearWelfareUnionIdLocal();
    };
  }, []);

  return <RouterProvider router={router} />;
}

export default App;
